import {
  dashboardRouteSearchSchema,
  type DashboardRouteSearch,
} from '../../dashboard/dashboardRouteSearch';
import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import { CampaignStatus, defaultCampaignsFilter, type CampaignsFilter } from './model';

export type CampaignsRouteSearch = DashboardRouteSearch & {
  agencies?: string;
  brand?: string;
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
};

export type CampaignsRouteFilters = Pick<
  CampaignsFilter,
  'brandId' | 'name' | 'status' | 'page' | 'size'
> & { agencyIds?: string[] };

export function campaignsRouteSearchSchema(search: Record<string, unknown>): CampaignsRouteSearch {
  const agencies = optionalTrimmedString(search['agencies']);
  const brand = optionalTrimmedString(search['brand']);
  const name = optionalTrimmedString(search['search']);
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'].trim() : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  return {
    ...dashboardRouteSearchSchema(search),
    ...(agencies ? { agencies } : {}),
    ...(brand ? { brand } : {}),
    ...(name ? { search: name } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
  };
}

export function parseCampaignsRouteSearch(search: CampaignsRouteSearch): CampaignsRouteFilters {
  return {
    ...(search.agencies
      ? {
          agencyIds: search.agencies
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean),
        }
      : {}),
    brandId: search.brand ?? defaultCampaignsFilter.brandId,
    name: search.search ?? defaultCampaignsFilter.name,
    status: parseStatuses(search.statuses),
    page: search.page ? search.page - 1 : defaultCampaignsFilter.page,
    size: search.size ?? defaultCampaignsFilter.size,
  };
}

export function serializeCampaignsRouteSearch(
  filters: CampaignsRouteFilters,
): CampaignsRouteSearch {
  return {
    ...(filters.agencyIds?.length ? { agencies: filters.agencyIds.join(',') } : {}),
    ...(filters.brandId ? { brand: filters.brandId } : {}),
    ...(filters.name.trim() ? { search: filters.name.trim() } : {}),
    ...(sameStatuses(filters.status, defaultCampaignsFilter.status)
      ? {}
      : { statuses: filters.status.join(',') }),
    ...(filters.page ? { page: filters.page + 1 } : {}),
    ...(filters.size !== defaultCampaignsFilter.size ? { size: filters.size } : {}),
  };
}

const campaignStatuses: ReadonlySet<string> = new Set(Object.values(CampaignStatus));

function parseStatuses(value: string | undefined) {
  if (value === undefined) return [...defaultCampaignsFilter.status];
  if (!value) return [];
  return value
    .split(',')
    .filter((status): status is CampaignStatus => campaignStatuses.has(status));
}

function sameStatuses(left: readonly CampaignStatus[], right: readonly CampaignStatus[]) {
  return left.length === right.length && left.every((status, index) => status === right[index]);
}
